import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Stack, Divider, Grid, Fab, Tooltip,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import {
  loadManagerKPIs, loadAllTrips, setFilter, setSelectedTrips,
} from '../../reduxcomponents/slices/tripManagementSlice';
import { ManagerKPICards } from './components/KPICards';
import TripsTable from './components/TripsTable';
import TripAssignmentDialog from './components/TripAssignmentDialog';
import RunningTripsMonitor from './components/RunningTripsMonitor';
import TripCalendar from './components/TripCalendar';
import PaymentDashboard from './components/PaymentDashboard';
import NotificationCenter from './components/NotificationCenter';
import { RaiseEscalationDialog } from './components/EscalationDialog';
import TripDetailDrawer from './components/TripDetailDrawer';

const TABS = [
  { label: '📋 All Trips',     value: 'trips' },
  { label: '🏃 Running',       value: 'running' },
  { label: '📅 Calendar',      value: 'calendar' },
  { label: '💰 Payments',      value: 'payments' },
  { label: '🔔 Reminders',     value: 'reminders' },
];

const ManagerDashboard = () => {
  const dispatch = useDispatch();
  const { managerKPIs, trips, loading, filters, selectedTripIds } = useSelector(s => s.tripManagement);

  const [tab, setTab] = useState('trips');
  const [assignDialog, setAssignDialog]  = useState({ open: false, trip: null, tripIds: null });
  const [escalateDialog, setEscalateDialog] = useState({ open: false, trip: null });
  const [detailDrawer, setDetailDrawer]  = useState({ open: false, trip: null });  // trip = full normalized object

  const refresh = () => {
    dispatch(loadManagerKPIs());
    dispatch(loadAllTrips(filters));
  };

  useEffect(() => {
    refresh();
  }, []);

  useEffect(() => {
    dispatch(loadAllTrips(filters));
  }, [filters]);

  const handleKPIFilter = (status) => {
    dispatch(setFilter({ status, page: 1 }));
    setTab('trips');
  };

  const handlePageChange = (page, limit) => {
    dispatch(setFilter({ page, ...(limit ? { limit } : {}) }));
  };

  const handleAssign = (trip) => {
    setAssignDialog({ open: true, trip: trip, tripIds: null });
  };

  const handleBulkAssign = (ids) => {
    setAssignDialog({ open: false, trip: null, tripIds: ids });
    setTimeout(() => setAssignDialog(d => ({ ...d, open: true })), 50);
  };

  const handleAssignSuccess = () => {
    dispatch(setSelectedTrips([]));
    refresh();
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Operations Manager Dashboard</Typography>
          <Typography variant="body2" color="text.secondary">
            Full visibility across all trips, executives, and payments
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <Fab size="small" color="primary" onClick={refresh} disabled={loading.kpis}>
            <Refresh />
          </Fab>
        </Tooltip>
      </Stack>

      {/* KPI Cards */}
      <Box mb={3}>
        <ManagerKPICards
          data={managerKPIs}
          loading={loading.kpis}
          onFilterClick={handleKPIFilter}
        />
      </Box>

      <Grid container spacing={3}>
        {/* Main Content */}
        <Grid item xs={12} lg={9}>
          <Paper variant="outlined" sx={{ borderRadius: 2 }}>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
            >
              {TABS.map(t => (
                <Tab key={t.value} label={t.label} value={t.value} sx={{ fontWeight: 600, fontSize: 13 }} />
              ))}
            </Tabs>

            <Box p={2.5}>
              {tab === 'trips' && (
                <TripsTable
                  trips={trips.data}
                  pagination={trips.pagination}
                  loading={loading.trips}
                  currentStatus={filters.status}
                  onPageChange={handlePageChange}
                  onStatusFilter={(s) => dispatch(setFilter({ status: s, page: 1 }))}
                  onRowClick={(trip) => setDetailDrawer({ open: true, trip })}
                  onAssign={handleAssign}
                  showAssign
                  selectedIds={selectedTripIds}
                  onSelectChange={(ids) => dispatch(setSelectedTrips(ids))}
                  onBulkAssign={handleBulkAssign}
                />
              )}

              {tab === 'running' && (
                <RunningTripsMonitor
                  onEscalate={(trip) => setEscalateDialog({ open: true, trip })}
                  onCall={(trip) => window.open(`tel:${trip.guest_id?.phone}`)}
                />
              )}

              {tab === 'calendar' && (
                <TripCalendar
                  onEventClick={({ tripId }) => {
                    const found = trips.data.find(t => t._id?.toString() === tripId?.toString());
                    if (found) setDetailDrawer({ open: true, trip: found });
                  }}
                />
              )}

              {tab === 'payments' && <PaymentDashboard />}

              {tab === 'reminders' && <NotificationCenter />}
            </Box>
          </Paper>
        </Grid>

        {/* Sidebar: Notifications */}
        <Grid item xs={12} lg={3}>
          <Paper variant="outlined" sx={{ borderRadius: 2, p: 2, height: '100%' }}>
            <NotificationCenter />
          </Paper>
        </Grid>
      </Grid>

      {/* Dialogs */}
      <TripAssignmentDialog
        open={assignDialog.open}
        trip={assignDialog.trip}
        tripIds={assignDialog.tripIds}
        onClose={() => setAssignDialog({ open: false, trip: null, tripIds: null })}
        onSuccess={handleAssignSuccess}
      />

      <RaiseEscalationDialog
        open={escalateDialog.open}
        tripId={escalateDialog.trip?._id}
        onClose={() => setEscalateDialog({ open: false, trip: null })}
        onSuccess={refresh}
      />

      <TripDetailDrawer
        open={detailDrawer.open}
        trip={detailDrawer.trip}
        onClose={() => setDetailDrawer({ open: false, trip: null })}
        isManager
      />
    </Box>
  );
};

export default ManagerDashboard;
